import { useState } from "react";
import {
  PlusIcon, PencilIcon, TrashIcon,
  StarIcon, MagnifyingGlassIcon, CheckIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../../../shared/components/LoadingSpinner";
import { useSpecialtiesManagement } from "../hooks/useSpecialtiesManagement";
import SpecialtyModal from "../components/SpecialtyModal";
import { useLanguage } from "../../../context/LanguageContext";

export default function SpecialtiesManagement() {
  const { t } = useLanguage();
  const {
    loading, filtered, stats,
    searchQuery, setSearchQuery,
    showModal, editingSpecialty, name, setName, options, setOptions,
    openCreate, openEdit, closeModal, save, remove,
  } = useSpecialtiesManagement();

  const [confirmId, setConfirmId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try { await remove(Number(confirmId)); } finally { setDeleting(false); setConfirmId(null); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50 pt-20 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <StarIcon className="w-6 h-6 text-[#b2d12e]" />
              <span className="text-sm font-semibold text-[#b2d12e] uppercase">{t("admin.specialtiesManagement.eyebrow")}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{t("admin.specialtiesManagement.title")}</h1>
            <p className="text-gray-600 mt-2">{t("admin.specialtiesManagement.subtitle")}</p>
          </div>
          <button onClick={openCreate}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[#b2d12e] to-[#a0c827] text-gray-900 rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 font-semibold">
            <PlusIcon className="w-5 h-5" /> {t("admin.specialtiesManagement.createSpecialty")}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {[
            { label: t("admin.specialtiesManagement.totalSpecialties"), value: stats.total, icon: <CheckIcon className="w-6 h-6 text-blue-600" />, bg: "bg-blue-100" },
            { label: t("admin.specialtiesManagement.searchResults"), value: stats.results, icon: <MagnifyingGlassIcon className="w-6 h-6 text-purple-600" />, bg: "bg-purple-100" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{s.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{s.value}</p>
                </div>
                <div className={`p-3 ${s.bg} rounded-lg`}>{s.icon}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={t("admin.specialtiesManagement.searchPlaceholder")} value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b2d12e] focus:border-transparent" />
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
            <PlusIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-900 mb-2">{t("admin.specialtiesManagement.noSpecialtiesFound")}</p>
            <p className="text-gray-600 mb-6">{t("admin.specialtiesManagement.noSpecialtiesHint")}</p>
            <button onClick={openCreate}
              className="inline-flex items-center gap-2 bg-[#b2d12e] text-gray-900 px-6 py-2 rounded-lg font-semibold hover:shadow-md transition-all">
              <PlusIcon className="w-5 h-5" /> {t("admin.specialtiesManagement.createSpecialty")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((specialty) => (
              <div key={specialty.id}
                className="group bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#b2d12e] transition-colors truncate">
                    {specialty.name}
                  </h3>
                </div>
                <div className="p-5">
                  <div className="space-y-3 mb-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t("admin.specialtiesManagement.created")}</p>
                      <p className="text-sm text-gray-600">{new Date(specialty.created_at).toLocaleDateString()}</p>
                    </div>
                    {(specialty.options?.length ?? 0) > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t("admin.specialtiesManagement.subOptions")}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {specialty.options!.map((o) => (
                            <span key={o.id} className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-0.5 rounded-full">
                              {o.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button onClick={() => openEdit(specialty)}
                      className="flex-1 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg font-medium transition-colors flex items-center justify-center gap-1">
                      <PencilIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">{t("common.edit")}</span>
                    </button>
                    <button onClick={() => setConfirmId(specialty.id)}
                      className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition-colors flex items-center justify-center gap-1">
                      <TrashIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">{t("common.delete")}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <SpecialtyModal
          isEditing={!!editingSpecialty}
          name={name}
          onNameChange={setName}
          options={options}
          onOptionsChange={setOptions}
          onSave={save}
          onClose={closeModal}
        />
      )}

      {confirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">{t("admin.specialtiesManagement.deleteConfirmTitle")}</h3>
            <p className="text-sm text-gray-500 mb-5">{t("common.thisActionCannotBeUndone")}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmId(null)} disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {deleting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {deleting ? t("common.deleting") : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
